'use client';

import React, { useState } from 'react';
import Link from 'next/link';
import Icon from '@/components/ui/AppIcon';

interface FlowNode {
  id: string;
  label: string;
  type: 'activity' | 'kpi' | 'bvo' | 'outcome';
  health: 'good' | 'warn' | 'bad';
  x: number;
  y: number;
}

interface FlowEdge {
  id: string;
  from: string;
  to: string;
}

const nodes: FlowNode[] = [
  { id: 'fn-order', label: 'Customer Order', type: 'activity', health: 'bad', x: 40, y: 80 },
  { id: 'fn-fulfill', label: 'Fulfillment', type: 'activity', health: 'warn', x: 160, y: 80 },
  { id: 'fn-invoice', label: 'Invoicing', type: 'activity', health: 'good', x: 280, y: 80 },
  { id: 'fn-collection', label: 'Cash Collection', type: 'activity', health: 'warn', x: 400, y: 80 },
  { id: 'fn-otd', label: 'On-Time Delivery %', type: 'kpi', health: 'bad', x: 100, y: 180 },
  { id: 'fn-dso', label: 'DSO (Days)', type: 'kpi', health: 'warn', x: 340, y: 180 },
  { id: 'fn-cust-order-bvo', label: 'Customer Order BVO', type: 'bvo', health: 'bad', x: 100, y: 270 },
  { id: 'fn-cash-bvo', label: 'Cash Application BVO', type: 'bvo', health: 'warn', x: 340, y: 270 },
  { id: 'fn-working-cap', label: 'Working Capital', type: 'outcome', health: 'warn', x: 220, y: 360 },
];

const edges: FlowEdge[] = [
  { id: 'fe-1', from: 'fn-order', to: 'fn-fulfill' },
  { id: 'fe-2', from: 'fn-fulfill', to: 'fn-invoice' },
  { id: 'fe-3', from: 'fn-invoice', to: 'fn-collection' },
  { id: 'fe-4', from: 'fn-order', to: 'fn-otd' },
  { id: 'fe-5', from: 'fn-fulfill', to: 'fn-otd' },
  { id: 'fe-6', from: 'fn-invoice', to: 'fn-dso' },
  { id: 'fe-7', from: 'fn-collection', to: 'fn-dso' },
  { id: 'fe-8', from: 'fn-otd', to: 'fn-cust-order-bvo' },
  { id: 'fe-9', from: 'fn-dso', to: 'fn-cash-bvo' },
  { id: 'fe-10', from: 'fn-cust-order-bvo', to: 'fn-working-cap' },
  { id: 'fe-11', from: 'fn-cash-bvo', to: 'fn-working-cap' },
];

const nodeColors: Record<string, { bg: string; border: string; text: string }> = {
  activity: { bg: '#1e3a5f', border: '#2563eb60', text: '#93c5fd' },
  kpi: { bg: '#0a2535', border: '#06b6d460', text: '#67e8f9' },
  bvo: { bg: '#2d1f4e', border: '#8b5cf660', text: '#c4b5fd' },
  outcome: { bg: '#1a3a2a', border: '#10b98160', text: '#6ee7b7' },
};

const healthStroke: Record<string, string> = {
  good: 'var(--positive)',
  warn: 'var(--warning)',
  bad: 'var(--negative)',
};

function getNodeCenter(node: FlowNode) {
  return { cx: node.x + 70, cy: node.y + 20 };
}

export default function ValueFlowMiniMap() {
  const [hoveredNode, setHoveredNode] = useState<string | null>(null);

  return (
    <div className="bg-card rounded-xl card-glow overflow-hidden">
      <div className="flex items-center justify-between px-5 py-4 border-b border-border">
        <div>
          <h2 className="text-sm font-semibold text-foreground">Business Value Flow</h2>
          <p className="text-xs text-muted-foreground mt-0.5">Order-to-Cash · End-to-End Value Chain</p>
        </div>
        <Link
          href="/business-value-flow-studio"
          className="flex items-center gap-1.5 text-xs text-primary hover:text-primary/80 font-medium transition-colors px-3 py-1.5 bg-primary/10 rounded-lg"
        >
          <Icon name="ArrowsPointingOutIcon" size={12} />
          Open Studio
        </Link>
      </div>

      <div className="p-4">
        {/* Legend */}
        <div className="flex items-center gap-4 mb-4 flex-wrap">
          {[
            { type: 'activity', label: 'Activity' },
            { type: 'kpi', label: 'KPI' },
            { type: 'bvo', label: 'BVO' },
            { type: 'outcome', label: 'Outcome' },
          ].map((item) => (
            <div key={`legend-${item.type}`} className="flex items-center gap-1.5">
              <div
                className="w-3 h-3 rounded-sm"
                style={{ background: nodeColors[item.type].bg, border: `1px solid ${nodeColors[item.type].border}` }}
              />
              <span className="text-2xs text-muted-foreground">{item.label}</span>
            </div>
          ))}
          <div className="ml-auto flex items-center gap-3">
            {[
              { color: 'var(--positive)', label: 'Good' },
              { color: 'var(--warning)', label: 'At Risk' },
              { color: 'var(--negative)', label: 'Critical' },
            ].map((h) => (
              <div key={`health-legend-${h.label}`} className="flex items-center gap-1">
                <div className="w-2.5 h-2.5 rounded-full" style={{ background: h.color }} />
                <span className="text-2xs text-muted-foreground">{h.label}</span>
              </div>
            ))}
          </div>
        </div>

        {/* SVG Canvas */}
        <div className="canvas-bg rounded-lg overflow-hidden">
          <svg viewBox="0 0 520 420" className="w-full h-auto" style={{ minHeight: '240px' }}>
            <defs>
              <marker id="arrowhead" markerWidth="8" markerHeight="6" refX="8" refY="3" orient="auto">
                <polygon points="0 0, 8 3, 0 6" fill="var(--border)" />
              </marker>
            </defs>

            {/* Edges */}
            {edges.map((edge) => {
              const fromNode = nodes.find((n) => n.id === edge.from);
              const toNode = nodes.find((n) => n.id === edge.to);
              if (!fromNode || !toNode) return null;
              const from = getNodeCenter(fromNode);
              const to = getNodeCenter(toNode);
              return (
                <line
                  key={edge.id}
                  x1={from.cx}
                  y1={from.cy}
                  x2={to.cx}
                  y2={to.cy}
                  stroke="var(--border)"
                  strokeWidth="1.5"
                  strokeDasharray="4 3"
                  markerEnd="url(#arrowhead)"
                  opacity="0.7"
                />
              );
            })}

            {/* Nodes */}
            {nodes.map((node) => {
              const colors = nodeColors[node.type];
              const isHovered = hoveredNode === node.id;
              return (
                <g
                  key={node.id}
                  transform={`translate(${node.x}, ${node.y})`}
                  onMouseEnter={() => setHoveredNode(node.id)}
                  onMouseLeave={() => setHoveredNode(null)}
                  style={{ cursor: 'pointer' }}
                >
                  <rect
                    x="0"
                    y="0"
                    width="140"
                    height="40"
                    rx="6"
                    fill={colors.bg}
                    stroke={isHovered ? healthStroke[node.health] : colors.border}
                    strokeWidth={isHovered ? 1.5 : 1}
                    opacity={isHovered ? 1 : 0.9}
                  />
                  <circle
                    cx="14"
                    cy="20"
                    r="5"
                    fill={healthStroke[node.health]}
                    opacity="0.9"
                  />
                  <text
                    x="26"
                    y="24"
                    fill={colors.text}
                    fontSize="9.5"
                    fontFamily="var(--font-sans)"
                    fontWeight="500"
                  >
                    {node.label}
                  </text>
                </g>
              );
            })}
          </svg>
        </div>
      </div>
    </div>
  );
}